import React from "react";
import { isDpatScoreRelaxationExempt } from "./dpatScoreRelaxation";

const LINK_CLASS = "px-2 text-primary fw-bold text-decoration-underline";

function extractHref(value) {
  if (value == null) return null;
  if (typeof value === "string" && /^https?:\/\//i.test(value)) return value;
  if (React.isValidElement(value) && value.props?.href) return value.props.href;
  if (typeof value === "object") {
    const href = value.props?.href ?? value.href ?? value.url;
    if (href) return href;
  }
  return null;
}

function extractLabel(value, defaultLabel) {
  const children = value?.props?.children;
  if (children == null) return defaultLabel;
  if (typeof children === "string") return children;
  if (Array.isArray(children)) {
    const text = children.filter((child) => typeof child === "string").join("");
    return text || defaultLabel;
  }
  return defaultLabel;
}

/** Renders a DPAT evidence link, "Not Uploaded", or a plain status string. */
export function renderDpatEvidenceLink(value, defaultLabel = "View Document") {
  if (value == null || value === "" || value === "Not Uploaded") {
    return "Not Uploaded";
  }

  const href = extractHref(value);
  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={LINK_CLASS}
        title="Click here to view the uploaded document"
      >
        {extractLabel(value, defaultLabel)}
      </a>
    );
  }

  if (typeof value === "string") {
    return value;
  }

  if (React.isValidElement(value)) {
    return value;
  }

  return "Not Uploaded";
}

/** True when a table cell holds an uploaded document/image used as verification evidence. */
export function isEvidenceLinkUploaded(value) {
  if (value == null || value === "" || value === "N/A") {
    return false;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed || /^not uploaded$/i.test(trimmed)) {
      return false;
    }
    if (/^there is no /i.test(trimmed) || /^no .*uploaded/i.test(trimmed)) {
      return false;
    }
    return /^https?:\/\//i.test(trimmed);
  }

  return Boolean(extractHref(value));
}

export function rowHasAllEvidenceLinks(row, linkKeys = []) {
  if (isDpatScoreRelaxationExempt()) {
    return true;
  }
  if (!linkKeys.length) {
    return true;
  }
  return linkKeys.every((key) => isEvidenceLinkUploaded(row?.[key]));
}

export function rowsHaveAllEvidenceLinks(rows, linkKeys = []) {
  if (isDpatScoreRelaxationExempt()) {
    return Boolean(rows?.length);
  }
  if (!rows?.length) {
    return false;
  }
  return rows.every((row) => rowHasAllEvidenceLinks(row, linkKeys));
}

export function allGroupedMeetingLinksUploaded(
  item,
  linkKeys = ["firstLink", "secondLink", "thirdLink"],
) {
  if (isDpatScoreRelaxationExempt()) {
    return Boolean(item);
  }
  if (!item) {
    return false;
  }
  return linkKeys.every((key) => isEvidenceLinkUploaded(item[key]));
}

export function allGroupedMeetingRowsLinksUploaded(
  rows,
  linkKeys = ["firstLink", "secondLink", "thirdLink"],
) {
  if (!rows?.length) {
    return false;
  }
  return rows.every((row) => allGroupedMeetingLinksUploaded(row, linkKeys));
}

export function documentHubLinksComplete(links = []) {
  if (isDpatScoreRelaxationExempt()) {
    return true;
  }
  if (!links?.length) {
    return false;
  }
  return links.every((item) => isEvidenceLinkUploaded(item?.link));
}

/** Awards a score only when all document-hub evidence links are uploaded. */
export function scoreOnlyIfDocumentLinksComplete(links, score) {
  if (isDpatScoreRelaxationExempt()) {
    return score;
  }
  if (!links?.length) {
    return score;
  }
  return documentHubLinksComplete(links) ? score : 0;
}

/** True when a non-link evidence field has a real value (not placeholder text). */
export function isTextEvidenceProvided(value) {
  if (value == null || value === "" || value === "N/A") {
    return false;
  }

  const trimmed = String(value).trim();
  if (!trimmed) {
    return false;
  }

  return !/^(not uploaded|not provided)$/i.test(trimmed);
}

export function rowsHaveAllProvidedValues(rows, keys = []) {
  if (!rows?.length || !keys.length) {
    return false;
  }

  return rows.every((row) => keys.every((key) => isTextEvidenceProvided(row?.[key])));
}

/** Awards a score only when every row has uploaded links in the given columns. */
export function scoreOnlyIfRowEvidenceComplete(rows, linkKeys, score, { minRows = 0 } = {}) {
  if (isDpatScoreRelaxationExempt()) {
    return score;
  }
  if (minRows > 0 && (!rows?.length || rows.length < minRows)) {
    return 0;
  }

  if (!rows?.length || !linkKeys?.length) {
    return 0;
  }

  return rowsHaveAllEvidenceLinks(rows, linkKeys) ? score : 0;
}

/** Awards a score only when every row has non-empty text evidence in the given columns. */
export function scoreOnlyIfRowValuesProvided(rows, valueKeys, score, { minRows = 0 } = {}) {
  if (isDpatScoreRelaxationExempt()) {
    return score;
  }
  if (minRows > 0 && (!rows?.length || rows.length < minRows)) {
    return 0;
  }

  if (!rows?.length || !valueKeys?.length) {
    return 0;
  }

  return rowsHaveAllProvidedValues(rows, valueKeys) ? score : 0;
}

/** Gates a score on document-hub links and/or per-row file links. */
export function scoreOnlyIfAllEvidenceComplete({
  rows,
  rowLinkKeys,
  documentLinks,
  score,
  minRows = 0,
}) {
  if (isDpatScoreRelaxationExempt()) {
    return score;
  }
  let result = score;

  if (documentLinks?.length) {
    result = scoreOnlyIfDocumentLinksComplete(documentLinks, result);
  }

  if (rowLinkKeys?.length) {
    result = scoreOnlyIfRowEvidenceComplete(rows, rowLinkKeys, result, { minRows });
  } else if (minRows > 0 && (!rows?.length || rows.length < minRows)) {
    return 0;
  }

  return result;
}

function hasMeetingDate(value) {
  return value != null && String(value).trim() !== "" && value !== "N/A";
}

/** CI 2.2: each sub-committee row must have 3 meeting dates and 3 uploaded minute links. */
export function statutorySubCommitteeRowComplete(item) {
  if (isDpatScoreRelaxationExempt()) {
    return Boolean(item);
  }
  if (!item) {
    return false;
  }

  const meetingSlots = ["firstMeeting", "secondMeeting", "thirdMeeting"];
  const linkSlots = ["firstLink", "secondLink", "thirdLink"];

  return meetingSlots.every((meetingKey, index) =>
    hasMeetingDate(item[meetingKey]) && isEvidenceLinkUploaded(item[linkSlots[index]]),
  );
}

export function allStatutorySubCommitteeRowsComplete(rows = []) {
  if (!rows?.length) {
    return false;
  }
  return rows.every((row) => statutorySubCommitteeRowComplete(row));
}

/** Applies {@link renderDpatEvidenceLink} to the given row keys. */
export function transformDpatTableRows(rows, linkKeys, labelByKey = {}) {
  if (!rows || !Array.isArray(rows)) return [];
  return rows.map((row) => {
    const next = { ...row };
    for (const key of linkKeys) {
      next[key] = renderDpatEvidenceLink(row[key], labelByKey[key]);
    }
    return next;
  });
}
